/**
 * Self-contained test page for departments: own Supabase client, auth, and APIs.
 * On tab visible: refresh session, wake-up, then auto-refetch list so load/insert work after leaving tab.
 * Route: /departments-test
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

const noOpLock = async (_name, _acquireTimeout, fn) => fn();

const testSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    lock: noOpLock,
  },
});

const FETCH_TIMEOUT_MS = 12000;
const INSERT_TIMEOUT_MS = 12000;

function withTimeout(promise, ms, label) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out after ${ms / 1000}s`)), ms)
    ),
  ]);
}

export default function DepartmentsTest() {
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [list, setList] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [insertLoading, setInsertLoading] = useState(false);
  const [error, setError] = useState(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const hasFetchedOnce = useRef(false);

  const runWakeUp = useCallback(() => {
    return withTimeout(
      testSupabase.from('profiles').select('id').limit(1),
      5000,
      'Wake-up'
    ).catch(() => {});
  }, []);

  const fetchDepartments = useCallback(async () => {
    setFetchLoading(true);
    setError(null);
    try {
      await runWakeUp();
      const result = await withTimeout(
        testSupabase.from('departments').select('id, code, name, status').order('name'),
        FETCH_TIMEOUT_MS,
        'Fetch'
      );
      const { data, error: err } = result;
      if (err) {
        setError(`Fetch error: ${err.message}`);
        setList([]);
      } else {
        setList(data || []);
        setLastRefreshed(new Date());
      }
    } catch (e) {
      setError(e?.message || 'Fetch failed');
      setList([]);
    } finally {
      setFetchLoading(false);
    }
  }, [runWakeUp]);

  const handleInsert = useCallback(async () => {
    const trimmedName = name.trim();
    const trimmedCode = code.trim() || trimmedName.slice(0, 4).toUpperCase();
    if (!trimmedName) {
      setError('Name is required');
      return;
    }
    setInsertLoading(true);
    setError(null);
    try {
      await runWakeUp();
      const result = await withTimeout(
        testSupabase
          .from('departments')
          .insert({
            code: trimmedCode,
            name: trimmedName,
            status: 'Active',
          })
          .select('id, code, name')
          .single(),
        INSERT_TIMEOUT_MS,
        'Insert'
      );
      const { data, error: err } = result;
      if (err) {
        setError(`Insert error: ${err.message}`);
      } else {
        setList((prev) => [...prev, data].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        setCode('');
        setName('');
        setLastRefreshed(new Date());
      }
    } catch (e) {
      setError(e?.message || 'Insert failed');
    } finally {
      setInsertLoading(false);
    }
  }, [name, code, runWakeUp]);

  const refreshSessionAndFetch = useCallback(async () => {
    setAuthLoading(true);
    setError(null);
    try {
      const { data: { session: s }, error: sessionErr } = await testSupabase.auth.getSession();
      if (sessionErr) {
        setError(`Session error: ${sessionErr.message}`);
        setAuthLoading(false);
        return;
      }
      setSession(s);
      await runWakeUp();
      setFetchLoading(true);
      const result = await withTimeout(
        testSupabase.from('departments').select('id, code, name, status').order('name'),
        FETCH_TIMEOUT_MS,
        'Fetch'
      );
      const { data, error: fetchErr } = result;
      if (fetchErr) {
        setError(`Fetch error: ${fetchErr.message}`);
        setList([]);
      } else {
        setList(data || []);
        setLastRefreshed(new Date());
      }
    } catch (e) {
      setError(e?.message || 'Refresh failed');
    } finally {
      setFetchLoading(false);
      setAuthLoading(false);
    }
  }, [runWakeUp]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { session: s }, error: sessionErr } = await testSupabase.auth.getSession();
      if (cancelled) return;
      setSession(s);
      setAuthLoading(false);
      if (sessionErr) {
        setError(`Session error: ${sessionErr.message}`);
        return;
      }
      await runWakeUp();
      if (cancelled) return;
      setFetchLoading(true);
      try {
        const result = await withTimeout(
          testSupabase.from('departments').select('id, code, name, status').order('name'),
          FETCH_TIMEOUT_MS,
          'Fetch'
        );
        if (cancelled) return;
        const { data, error: fetchErr } = result;
        if (fetchErr) {
          setError(`Fetch error: ${fetchErr.message}`);
          setList([]);
        } else {
          setList(data || []);
          setLastRefreshed(new Date());
          hasFetchedOnce.current = true;
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Fetch failed');
      } finally {
        if (!cancelled) setFetchLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [runWakeUp]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshSessionAndFetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSessionAndFetch]);

  if (authLoading && !hasFetchedOnce.current) {
    return (
      <div style={{ padding: '1.5rem' }}>
        <h1>Departments test</h1>
        <p>Loading session &amp; departments…</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: 560 }}>
      <h1 style={{ marginBottom: '0.5rem' }}>Departments test</h1>
      <p style={{ color: '#666', marginBottom: '0.5rem', fontSize: 14 }}>
        Own Supabase client + auth. Get/post run here; on tab visible we refresh session, wake-up, then refetch.
      </p>
      {session ? (
        <p style={{ fontSize: 12, color: '#22c55e', marginBottom: '1rem' }}>
          Signed in: {session.user?.email ?? session.user?.id}
        </p>
      ) : (
        <p style={{ fontSize: 12, color: '#b91c1c', marginBottom: '1rem' }}>No session (use main app login first, then open this route)</p>
      )}
      {lastRefreshed && (
        <p style={{ fontSize: 12, color: '#666', marginBottom: '1rem' }}>
          Last refreshed: {lastRefreshed.toLocaleTimeString()}
        </p>
      )}

      <div style={{ marginBottom: '1rem', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={fetchDepartments}
          disabled={fetchLoading}
          style={{ padding: '8px 16px' }}
        >
          {fetchLoading ? 'Loading…' : 'Load departments'}
        </button>
        <button
          type="button"
          onClick={refreshSessionAndFetch}
          disabled={authLoading || fetchLoading}
          style={{ padding: '8px 16px' }}
        >
          Refresh session + fetch
        </button>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: 12,
            marginBottom: 12,
            background: '#fef2f2',
            color: '#b91c1c',
            borderRadius: 8,
          }}
        >
          {error}
        </div>
      )}

      <section style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>Insert one</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 320 }}>
          <label>
            Code
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g. TEST"
              style={{ display: 'block', marginTop: 4, padding: 8, width: '100%' }}
            />
          </label>
          <label>
            Name
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Test Dept"
              style={{ display: 'block', marginTop: 4, padding: 8, width: '100%' }}
            />
          </label>
          <button
            type="button"
            onClick={handleInsert}
            disabled={insertLoading || !name.trim()}
            style={{ padding: '8px 16px', alignSelf: 'flex-start' }}
          >
            {insertLoading ? 'Inserting…' : 'Insert'}
          </button>
        </div>
      </section>

      <section>
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>
          List ({list.length}) {fetchLoading && '(loading…)'}
        </h2>
        {list.length === 0 && !fetchLoading && (
          <p style={{ color: '#666' }}>List will load on mount and when you return to this tab.</p>
        )}
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {list.map((row) => (
            <li
              key={row.id}
              style={{ padding: '6px 0', borderBottom: '1px solid #eee' }}
            >
              <strong>{row.code}</strong> — {row.name} ({row.status || '—'})
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
